import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Alert,
  useTheme,
  styled,
  alpha,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { SHADOWS } from '../../styles/theme';
import '../../index.css';

import { RoiWorkViewProps, DRAWER_WIDTH, RIGHT_DRAWER_WIDTH } from './types';
import { useRoiFileManagement } from './hooks/useRoiFileManagement';
import { useRoiEditing } from './hooks/useRoiEditing';
import { useDualViewSizing } from './hooks/useDualViewSizing';
import { RoiWorkHeader } from './components/RoiWorkHeader';
import { RoiWorkLeftSidebar } from './components/RoiWorkLeftSidebar';
import { RoiWorkCanvasArea } from './components/RoiWorkCanvas';
import { RoiWorkRightPanel } from './components/RoiWorkRightPanel';
import { RoiWorkModals } from './components/RoiWorkModals';
import { RoiWorkMapPanel } from './components/RoiWorkMapPanel';

// Styled Components
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'openLeft' && prop !== 'openRight' })<{
  openLeft?: boolean;
  openRight?: boolean;
}>(({ theme, openLeft, openRight }) => ({
  flexGrow: 1,
  height: '100vh',
  marginTop: 0,
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: -DRAWER_WIDTH,
  marginRight: -RIGHT_DRAWER_WIDTH,
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  ...(openLeft && {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  ...(openRight && {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  }),
}));

export const RoiWorkView: React.FC<RoiWorkViewProps> = ({ projectId: propProjectId, onBack: _onBack }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const params = useParams<{ projectId: string }>();

  const projectId = params.projectId || propProjectId || '';

  const [leftDrawerOpen, setLeftDrawerOpen] = useState(true);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(true);
  const [showDualView] = useState(true);

  const fileManagement = useRoiFileManagement(projectId);
  const [mapPanelOpen, setMapPanelOpen] = useState(true);
  const editing = useRoiEditing({
    roiData: fileManagement.roiData,
    setRoiData: fileManagement.setRoiData,
    selectedRoiFile: fileManagement.selectedRoiFile,
    selectedCctv: fileManagement.selectedCctv,
    projectId,
    setError: fileManagement.setError,
    setSuccess: fileManagement.setSuccess,
    setHasUnsavedChanges: fileManagement.setHasUnsavedChanges,
    cctvRoiDataMap: fileManagement.cctvRoiDataMap,
    setCctvRoiDataMap: fileManagement.setCctvRoiDataMap,
    originalCctvDataMap: fileManagement.originalCctvDataMap,
    cctvWithChanges: fileManagement.cctvWithChanges,
    setCctvWithChanges: fileManagement.setCctvWithChanges,
    loadRoiLists: fileManagement.loadRoiLists,
  });

  const { referenceContainerRef, workspaceContainerRef, unifiedSizes } =
    useDualViewSizing(showDualView, leftDrawerOpen, rightDrawerOpen);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      <RoiWorkHeader projectId={projectId} />

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        <RoiWorkLeftSidebar
          leftDrawerOpen={leftDrawerOpen}
          roiFiles={fileManagement.roiFiles}
          selectedRoiFile={fileManagement.selectedRoiFile}
          onRoiFileSelect={fileManagement.handleRoiFileSelect}
          cctvList={fileManagement.cctvList}
          selectedCctv={fileManagement.selectedCctv}
          onCctvSelect={fileManagement.handleCctvSelect}
          cctvWithChanges={fileManagement.cctvWithChanges}
        />

        <Main openLeft={leftDrawerOpen} openRight={rightDrawerOpen} sx={{ height: '100vh', marginTop: 0 }}>
          {/* Helper/Expand Button (Left) */}
          <Box sx={{ position: 'absolute', top: '50%', left: 10, transform: 'translateY(-50%)', zIndex: 10 }}>
            <IconButton
              size="small"
              onClick={() => setLeftDrawerOpen(!leftDrawerOpen)}
              className="hover-lift"
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.secondary',
                boxShadow: isDark ? SHADOWS.dark.sm : SHADOWS.light.sm,
                '&:hover': {
                  bgcolor: 'background.paper',
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  boxShadow: isDark ? SHADOWS.dark.md : SHADOWS.light.md
                }
              }}
            >
              {leftDrawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Box>

          {/* Properties Panel Toggle Button (Right) */}
          <Box sx={{ position: 'absolute', top: '50%', right: 10, transform: 'translateY(-50%)', zIndex: 10 }}>
            <IconButton
              size="small"
              onClick={() => setRightDrawerOpen(!rightDrawerOpen)}
              className="hover-lift"
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.secondary',
                boxShadow: isDark ? SHADOWS.dark.sm : SHADOWS.light.sm,
                '&:hover': {
                  bgcolor: 'background.paper',
                  color: 'info.main',
                  borderColor: 'info.main',
                  boxShadow: isDark ? SHADOWS.dark.md : SHADOWS.light.md
                }
              }}
            >
              {rightDrawerOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Box>

          {/* 맵 도면 — CCTV 위치 확인, 마커 클릭으로 CCTV 전환 */}
          <RoiWorkMapPanel
            projectId={projectId}
            open={mapPanelOpen}
            onToggle={() => setMapPanelOpen((v) => !v)}
            selectedCctv={fileManagement.selectedCctv}
            onCctvSelect={fileManagement.handleCctvSelect}
            onMapCctvs={fileManagement.applyMapCctvs}
          />

          <RoiWorkCanvasArea
            showDualView={showDualView}
            isDark={isDark}
            referenceContainerRef={referenceContainerRef}
            originalImageUrl={fileManagement.originalImageUrl}
            originalCctvDataMap={fileManagement.originalCctvDataMap}
            selectedCctv={fileManagement.selectedCctv}
            selectedRoiId={editing.selectedRoiId}
            setSelectedRoiId={editing.setSelectedRoiId}
            setRightDrawerOpen={setRightDrawerOpen}
            unifiedSizes={unifiedSizes}
            workspaceContainerRef={workspaceContainerRef}
            cctvImageUrl={fileManagement.cctvImageUrl}
            roiData={fileManagement.roiData}
            roiCanvasRef={editing.roiCanvasRef}
            roiEditMode={editing.roiEditMode}
            setRoiEditMode={editing.setRoiEditMode}
            editingRoiId={editing.editingRoiId}
            setEditingRoiId={editing.setEditingRoiId}
            editingOriginalRoi={editing.editingOriginalRoi}
            setEditingOriginalRoi={editing.setEditingOriginalRoi}
            isSelectingRoiForEdit={editing.isSelectingRoiForEdit}
            setIsSelectingRoiForEdit={editing.setIsSelectingRoiForEdit}
            currentDrawingPoints={editing.currentDrawingPoints}
            setCurrentDrawingPoints={editing.setCurrentDrawingPoints}
            hasUnsavedChanges={fileManagement.hasUnsavedChanges}
            setError={fileManagement.setError}
            handleCreateRoi={editing.handleCreateRoi}
            handleUpdateRoi={editing.handleUpdateRoi}
            handleConfirmDelete={editing.handleConfirmDelete}
            handleSave={editing.handleSave}
            handleFinalSave={editing.handleFinalSave}
            handleRollback={editing.handleRollback}
            setRoiData={fileManagement.setRoiData}
            selectedRoiFile={fileManagement.selectedRoiFile}
          />
        </Main>

        <RoiWorkRightPanel
          rightDrawerOpen={rightDrawerOpen}
          selectedRoiId={editing.selectedRoiId}
          setSelectedRoiId={editing.setSelectedRoiId}
          roiData={fileManagement.roiData}
        />
      </Box>

      {/* Alerts */}
      {fileManagement.error && (
        <Alert
          severity="error"
          onClose={() => fileManagement.setError('')}
          className="animate-slide-up"
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            boxShadow: isDark ? SHADOWS.dark.lg : SHADOWS.light.lg,
            border: '1px solid',
            borderColor: alpha(theme.palette.error.main, 0.3)
          }}
        >
          {fileManagement.error}
        </Alert>
      )}
      {fileManagement.success && (
        <Alert
          severity="success"
          onClose={() => fileManagement.setSuccess('')}
          className="animate-slide-up"
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            boxShadow: isDark ? SHADOWS.dark.lg : SHADOWS.light.lg,
            border: '1px solid',
            borderColor: alpha(theme.palette.success.main, 0.3)
          }}
        >
          {fileManagement.success}
        </Alert>
      )}

      <RoiWorkModals
        showRoiIdDialog={editing.showRoiIdDialog}
        setShowRoiIdDialog={editing.setShowRoiIdDialog}
        newRoiId={editing.newRoiId}
        setNewRoiId={editing.setNewRoiId}
        handleConfirmRoiId={editing.handleConfirmRoiId}
        setPendingCreatePoints={editing.setPendingCreatePoints}
        setRoiEditMode={editing.setRoiEditMode}
        roiCanvasRef={editing.roiCanvasRef}
        setCurrentDrawingPoints={editing.setCurrentDrawingPoints}
        showSaveDialog={editing.showSaveDialog}
        setShowSaveDialog={editing.setShowSaveDialog}
        handleConfirmSave={editing.handleConfirmSave}
        selectedRoiFile={fileManagement.selectedRoiFile}
      />
    </Box>
  );
};

export default RoiWorkView;
